import React, { forwardRef } from 'react';
import { any,
  isEmpty,
  isNil,
  length,
  map,
  prop,
  replace,
  slice,
  toLower } from 'ramda';
import { isNilOrEmpty, mapIndexed } from 'ramda-adjunct';

const DIACRITICS_REGEX = /[\u0300-\u036f]/g;

const normalizeForSearch = (value) => replace(DIACRITICS_REGEX, '', value.normalize('NFD'));

const buildNormalizedTextWithIndexMap = (text) => {
  let normalizedText = '';
  const indexMap = [];

  for (let index = 0; index < length(text); index += 1) {
    const normalizedChar = normalizeForSearch(text[index]);

    for (let offset = 0; offset < length(normalizedChar); offset += 1) {
      normalizedText += normalizedChar[offset];
      indexMap.push(index);
    }
  }

  return { normalizedText, indexMap };
};

const getHighlightRanges = (text, term) => {
  const normalizedTerm = toLower(normalizeForSearch(term));
  const { normalizedText, indexMap } = buildNormalizedTextWithIndexMap(text);
  const lowerNormalizedText = toLower(normalizedText);
  const ranges = [];
  const normalizedTermLength = length(normalizedTerm);
  const normalizedTextLength = length(lowerNormalizedText);

  if (normalizedTermLength === 0) return [];

  let cursor = 0;
  while (cursor <= normalizedTextLength - normalizedTermLength) {
    const startInNormalized = lowerNormalizedText.indexOf(normalizedTerm, cursor);
    if (startInNormalized === -1) break;

    const endInNormalized = startInNormalized + normalizedTermLength - 1;
    const startInOriginal = indexMap[startInNormalized];
    const endInOriginal = indexMap[endInNormalized] + 1;

    ranges.push({ start: startInOriginal, end: endInOriginal });
    cursor = startInNormalized + normalizedTermLength;
  }

  return ranges;
};

const splitSegments = (text, term) => {
  if (isNilOrEmpty(term) || isNilOrEmpty(text)) return [{ text: text || '', highlight: false }];

  const ranges = getHighlightRanges(text, term);
  if (isEmpty(ranges)) return [{ text, highlight: false }];

  const textLength = length(text);
  const segments = [];
  let cursor = 0;

  map(({ start, end }) => {
    if (start > cursor) {
      segments.push({ text: slice(cursor, start, text), highlight: false });
    }

    segments.push({ text: slice(start, end, text), highlight: true });
    cursor = end;
    return null;
  }, ranges);

  if (cursor < textLength) {
    segments.push({ text: slice(cursor, textLength, text), highlight: false });
  }

  return segments;
};

const HighlightText = forwardRef(({ text, searchTerm, className }, ref) => {
  if (isNil(text)) return null;

  const segments = splitSegments(text, searchTerm);
  const hasHighlight = isEmpty(searchTerm) ? false : any(prop('highlight'), segments);

  if (!hasHighlight) {
    return <span ref={ref} className={className}>{text}</span>;
  }

  return (
    <span ref={ref} className={className}>
      {mapIndexed((segment, index) => {
        if (prop('highlight', segment)) {
          return (
            <mark
              key={index}
              className="bg-flexi-history-highlight p-0"
            >
              {prop('text', segment)}
            </mark>
          );
        }

        return <span key={index}>{prop('text', segment)}</span>;
      }, segments)}
    </span>
  );
});

HighlightText.displayName = 'HighlightText';

export default HighlightText;
